/*eslint react-hooks/exhaustive-deps: "off"*/
import React, { useCallback, useEffect, useRef, useState } from "react";
import Axios from "axios";
import { sum } from "d3";
import { cloneDeep } from "lodash";
import useDebounce from "../../utils/hooks/useDebounce";
import useInputs from "../../utils/hooks/useInputs";
import {
  getApiHost,
  getReadableFreq,
  numberWithCommas,
  msgClose,
  getReadableFreqNoUnit,
} from "../../utils";
import USAGE_COLORS from "./usage-colors";
import arrowImg from "../../images/arrow.png";
import FreqLinesChart from "./freq-lines";
import {
  SPECTRUM_FRQ_HZ,
  SPECTRUM_SIDO_CHECK_LIST,
  SPECTRUM_UCODE_CHECK_LIST,
  SPECTRUM_USAGE_CHECK_LIST,
} from "../../Constants";
import { message } from "antd";

const INITIAL_INPUTS_STATE = {
  frq_hz: SPECTRUM_FRQ_HZ,
  sidoCheckList: SPECTRUM_SIDO_CHECK_LIST,
  ucodeCheckList: SPECTRUM_UCODE_CHECK_LIST,
  usageCheckList: SPECTRUM_USAGE_CHECK_LIST,
};

export default function useSpectrumProps({ lineHeight }) {
  const [activePageTab, setActivePageTab] = useState(0);

  const containerRef = useRef();
  const chartRef = useRef();
  const [chartWidth, setChartWidth] = useState();

  const [inputsState, onChangeInputsState] = useInputs(INITIAL_INPUTS_STATE);

  const [originChartData, setOriginChartData] = useState([]);

  const [enableGuide, setEnableGuide] = useState(false);
  const [enableSpace, setEnableSpace] = useState(false);
  const [enableDraw, setEnableDraw] = useState(false);

  const [ucodeList, setUcodeList] = useState([]);
  const [usageList, setUsageList] = useState([]);
  const [usageDict, setUsageDict] = useState({});
  const [sidoList, setSidoList] = useState([]);

  const [bandwidthKHzDict, setBandwidthKHzDict] = useState({});

  const [
    statsByAlmBusNmAndDistrictLv1,
    setStatsByAlmBusNmAndDistrictLv1,
  ] = useState({});
  const [activeDistrictLv1CodeList, setActiveDistrictLv1CodeList] = useState(
    [],
  );
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isLoadingFreq, setIsLoadingFreq] = useState(false);

  message.config({
    top: 250,
    duration: 15,
    maxCount: 1,
  });

  const infoByZoom = () => {
    message.info({
      content: (
        <span style={{ fontSize: 15 }}> 시작 주파수 값이 종료보다 큽니다.</span>
      ),
    });
    msgClose();
  };

  // Chart Guide 활성화 여부를 차트에 반영합니다.
  useEffect(() => {
    const { bgn_frq_hz, end_frq_hz } = inputsState;
    if (bgn_frq_hz && end_frq_hz && chartRef.current) {
      chartRef.current.enableGuide(enableGuide);
      chartRef.current.enableSpace(enableSpace);
      chartRef.current.enableDraw(enableDraw);
    }
  }, [chartRef.current, enableGuide, enableSpace, enableDraw]);

  // 페이지의 width가 변경되면, 이를 차트에 반영합니다.
  useDebounce(
    () => {
      const { bgn_frq_hz, end_frq_hz } = inputsState;
      if (bgn_frq_hz && end_frq_hz && chartRef.current && chartWidth)
        chartRef.current.width(chartWidth);
    },
    400,
    [chartWidth],
  );

  // 초기화 : 주파수 Geojson 데이터 로딩
  useEffect(() => {
    const url =
      window.SPECTRUM_USE_KEY_URL ||
      getApiHost() + "/geojson/spectrum_use_key.geojson";
    Axios.get(url)
      .then(({ data }) => {
        setOriginChartData(data);
      })
      .catch(e => console.error(e));
  }, []);

  // 그래프/테이블 Tab이 변경될 시
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.hideClickedTooltip();
    }
  }, [activePageTab, isLoadingStats]);

  // 초기화: 구분 데이터 로딩
  //useEffect(() => {
  //  loadUcodeList();
  //}, []);

  // "구분" 선택내역이 변경되면, 그에 맞춰 "용도" 목록을 갱신합니다.
  useEffect(() => {
    const { bgn_frq_hz, end_frq_hz, frq_hz, ucodeCheckList } = inputsState;

    if (bgn_frq_hz && end_frq_hz) {
      const url = getApiHost() + "/useSearch_Preset_UcodeChange.json";
      const formData = new FormData();
      formData.append("bgn_frq_hz", bgn_frq_hz);
      formData.append("end_frq_hz", end_frq_hz);
      formData.append("frq_hz", frq_hz);
      formData.append("ucodeCheckList", ucodeCheckList.join("|"));

      Axios.post(url, formData)
        .then(({ data }) => {
          const { usageList } = data;
          setUsageList(
            usageList.map(({ ALM_BUS_NM }) => ({
              value: ALM_BUS_NM,
              label: ALM_BUS_NM,
            })),
          );
          inputsState.usageCheckList = inputsState.usageCheckList.filter(
            val =>
              usageList
                .map(function (d) {
                  return d["ALM_BUS_NM"];
                })
                .indexOf(val) > -1,
          );
        })
        .catch(e => {
          console.error(e);
        });
    }
  }, [inputsState.ucodeCheckList]);

  // 변경된 originChartData, usageDict, statsByAlmBusNmAndDistrictLv1를 차트에 반영하여 그립니다.
  useEffect(() => {
    const { usageCheckList, bgn_frq_hz, end_frq_hz, frq_hz } = inputsState;
    if (chartRef.current && bgn_frq_hz && end_frq_hz) {
      const start_freq = bgn_frq_hz * frq_hz;
      const end_freq = end_frq_hz * frq_hz;

      const sizePerGroup = Object.entries(statsByAlmBusNmAndDistrictLv1).reduce(
        (acc, [groupName, stats]) => {
          let statsArray = Object.values(stats);
          statsArray.pop();
          acc[groupName] = sum(statsArray);
          return acc;
        },
        {},
      );

      const chartData = cloneDeep(originChartData)
        .map(row => {
          const { data } = row;
          for (let i = 0; i < data.length; i++) {
            data[i]["data"] = data[i]["data"].filter(({ valueRange }) => {
              const isInvalid =
                (valueRange[0] < start_freq && valueRange[1] < start_freq) ||
                (valueRange[0] > end_freq && valueRange[1] > end_freq);
              return !isInvalid;
            });
          }
          return { ...row, data };
        })
        .filter(({ group }) => usageCheckList.includes(group));

      const _bandwidthKHzDict = {};

      chartData.forEach(groupRow => {
        const groupName = groupRow.group;
        const groupDataList = groupRow.data;
        groupDataList.forEach(labelRow => {
          const labelDataList = labelRow.data;
          const size = sizePerGroup[groupName] || 0; // 무선국수
          const bandwidth_hz = labelDataList.reduce((acc, { valueRange }) => {
            acc += valueRange[1] - valueRange[0];
            return acc;
          }, 0);
          const bandwidth_khz = Number((bandwidth_hz / 1000).toFixed(2));
          const ratio =
            bandwidth_khz > 0 ? Number((size / bandwidth_khz).toFixed(2)) : 0;

          const sizeLabel = size > 0 ? numberWithCommas(size) : "-";
          const bandwidthLabel =
            bandwidth_khz > 0 ? numberWithCommas(bandwidth_khz) : "-";
          const ratioLabel = ratio > 0 ? numberWithCommas(ratio) : "-";

          labelRow.val1 = sizeLabel;
          labelRow.val2 = bandwidthLabel;
          labelRow.val3 = ratioLabel;

          _bandwidthKHzDict[groupName] = bandwidthLabel;
        });
      });

      setBandwidthKHzDict(_bandwidthKHzDict);

      setActiveDistrictLv1CodeList([
        ...new Set(
          Object.values(statsByAlmBusNmAndDistrictLv1)
            .map(row => Object.keys(row))
            .flat(),
        ),
      ]);

      let dataLength = 0;
      Object.values(originChartData).forEach(({ data }) => {
        data.forEach(({ data }) => {
          dataLength += data.length;
        });
      });

      if (!isLoadingFreq) {
        chartRef.current
          .maxHeight(lineHeight * dataLength)
          .zColorScale(d => {
            const groupName = d.group;
            const usageCode = usageDict[groupName];
            const usageColor = USAGE_COLORS[usageCode] || USAGE_COLORS["null"];
            return usageColor["background-color"];
          })
          .tooltipHtml((groupName, [freqStart, freqEnd]) => {
            return `
              <div
              style="
                width: 350px;
                padding: 20px;
                background-color: white;
                box-shadow: 0px 3px 5px 0px rgba(161, 161, 161, 1);
              "
            >
              <div
                className="title"
                style="
                  height: 40px;
                  line-height: 40px;
                  color: white;
                  padding: 0 20px;
                  background-color: #656a78;
                  margin: -20px -20px 20px -20px;
                  font-size: 14px;
                  font-weight: bold;
                "
              >
              ${groupName}
              </div>
              <div
                className="bandswith"
                style="
                  width: 90px;
                  height: 90px;
                  border-radius: 50%;
                  margin: 0 auto;
                  background-color: #EA5514;
                  padding: 10px;
                  color: white;
                  font-weight: bold;
                  text-align: center;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                "
              >
                대역<br />
                ${getReadableFreq(freqEnd - freqStart) || "0 Hz"}
              </div>

              <div style="padding: 20px 0; text-align: center;">
                <img
                  className="arrow"
                  src="${arrowImg}"
                  style="display: block; width: 230px; margin: 0 auto;"
                />
                <div
                  style="
                    width: 270px;
                    margin: 10px auto;
                    display: flex;
                    justify-content: space-between;
                    font-weight: bold;
                  "
                >
                  <div className="start-freq" style="display: inline-block;">
                    시작<br />
                    ${getReadableFreq(freqStart)}
                  </div>
                  <div className="end-freq" style="display: inline-block;">
                    종료<br />
                    ${getReadableFreq(freqEnd)}
                  </div>
                </div>
              </div>
            </div>
          `;
          })
          .data(chartData);

        chartRef.current.zoomX([bgn_frq_hz * frq_hz, end_frq_hz * frq_hz]);
      }
    }
  }, [originChartData, usageDict, statsByAlmBusNmAndDistrictLv1]);

  const loadUcodeList = useCallback(() => {
    const { bgn_frq_hz, end_frq_hz, frq_hz } = inputsState;

    if (!(bgn_frq_hz && end_frq_hz)) {
      return;
    }

    if (Number(bgn_frq_hz) > Number(end_frq_hz)) {
      infoByZoom();
      return;
    }

    const urlList = [
      getApiHost() + "/useSearch_Preset_Ucode.json",
      getApiHost() + "/useSearch_Preset_Usage.json",
      getApiHost() + "/useSearch_Distirct.json",
    ];

    const formData = new FormData();
    formData.append("bgn_frq_hz", bgn_frq_hz);
    formData.append("end_frq_hz", end_frq_hz);
    formData.append("frq_hz", frq_hz);

    setIsLoadingFreq(true);

    Axios.all(urlList.map(url => Axios.post(url, formData)))
      .then(
        Axios.spread((...responseList) => {
          const { ucodeList } = responseList[0].data;
          const { usageList } = responseList[1].data;
          const { distirctList } = responseList[2].data;

          setSidoList(
            distirctList.map(({ DISTRICT_CODE, DISTRICT_NM }) => ({
              value: DISTRICT_CODE,
              label: DISTRICT_NM,
            })),
          );

          //inputsState.sidoCheckList = inputsState.sidoCheckList.filter(val => distirctList.map(function(d) {return d["DISTRICT_CODE"];}).indexOf(val) > -1);
          inputsState.sidoCheckList = distirctList.map(
            ({ DISTRICT_CODE }) => DISTRICT_CODE,
          );

          setUcodeList(
            ucodeList.map(({ CODE_ID, CODE_ID_NM }) => ({
              value: CODE_ID,
              label: CODE_ID_NM,
            })),
          );

          //inputsState.ucodeCheckList = inputsState.ucodeCheckList.filter(val => ucodeList.map(function(d) {return d["CODE_ID"];}).indexOf(val) > -1);
          inputsState.ucodeCheckList = ucodeList.map(({ CODE_ID }) => CODE_ID);

          setUsageDict(
            usageList.reduce((acc, { ALM_BUS_NM, ALM_BUS_CD }) => {
              acc[ALM_BUS_NM] = ALM_BUS_CD;
              return acc;
            }, {}),
          );

          //inputsState.usageCheckList = inputsState.usageCheckList.filter(val => usageList.map(function(d) {return d["ALM_BUS_NM"];}).indexOf(val) > -1);
          inputsState.usageCheckList = usageList.map(
            ({ ALM_BUS_NM }) => ALM_BUS_NM,
          );

          setIsLoadingFreq(false);
        }),
      )
      .catch(error => {
        console.error(error);
      });
  }, [inputsState]);

  const onClickShowChart = useCallback(() => {
    const {
      bgn_frq_hz,
      end_frq_hz,
      frq_hz,
      sidoCheckList,
      ucodeCheckList,
      usageCheckList,
    } = inputsState;

    const container = containerRef.current;
    if (!chartRef.current) {
      chartRef.current = FreqLinesChart()(container)
        .xTickFormat(value => getReadableFreqNoUnit(value))
        .width(container.offsetWidth)
        .maxLineHeight(lineHeight);
    }

    // 그래프보기 먼저 보여주기 위한 설정
    setStatsByAlmBusNmAndDistrictLv1({});

    if (Number(bgn_frq_hz) > Number(end_frq_hz)) {
      infoByZoom();
      return;
    }

    const url = getApiHost() + "/useSearchStat.json";
    const formData = new FormData();
    formData.append("bgn_frq_hz", bgn_frq_hz);
    formData.append("end_frq_hz", end_frq_hz);
    formData.append("frq_hz", frq_hz);
    formData.append("sidoCheckList", sidoCheckList.join("|"));
    formData.append("ucodeCheckList", ucodeCheckList.join("|"));
    formData.append("usageCheckList", usageCheckList.join("|"));

    setIsLoadingStats(true);

    Axios.post(url, formData)
      .then(({ data }) => {
        // FIXME: 응답 중에 stats_by_alm_bus_nm 은 사용하고 있지 않습니다.
        const { stats_by_alm_bus_nm_and_district_lv1 } = data;
        setStatsByAlmBusNmAndDistrictLv1(stats_by_alm_bus_nm_and_district_lv1);
      })
      .catch(reason => console.error(reason))
      .finally(() => {
        setIsLoadingStats(false);
      });
  }, [inputsState]);

  return {
    activePageTab,
    setActivePageTab,
    containerRef,
    chartRef,
    chartWidth,
    setChartWidth,

    inputsState,
    onChangeInputsState,
    originChartData,
    setOriginChartData,

    enableGuide,
    setEnableGuide,
    enableSpace,
    setEnableSpace,
    enableDraw,
    setEnableDraw,

    ucodeList,
    setUcodeList,
    loadUcodeList,
    usageList,
    setUsageList,
    usageDict,
    setUsageDict,
    bandwidthKHzDict,
    setBandwidthKHzDict,

    statsByAlmBusNmAndDistrictLv1,
    setStatsByAlmBusNmAndDistrictLv1,
    activeDistrictLv1CodeList,
    setActiveDistrictLv1CodeList,

    isLoadingStats,
    setIsLoadingStats,
    isLoadingFreq,
    setIsLoadingFreq,

    onClickShowChart,

    sidoList,
    setSidoList,
  };
}
